import { Directive, ElementRef, OnInit, Output, EventEmitter, Renderer2, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from 'src/app/utils/take-until-destroyed';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { FarmoutService } from 'src/app/service/farmout-service/farmout.service';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';


declare var google: any;

@Directive({
    selector: '[custom-place], [google-place]'
})
export class CustomPlaceDirective implements OnInit, OnDestroy, OnChanges {
    @Input() radius: any;
    @Output() onSelect: EventEmitter<any> = new EventEmitter();

    private element: HTMLInputElement;
    private dropdown: HTMLElement;
    private destroy$ = new Subject<void>();
    private user: any;
    private isInitialized = false;
    private googleAutocomplete: any;
    private customPickerActive = false;
    private isFromSuggestionSelection = false;

    constructor(
        elRef: ElementRef,
        private renderer: Renderer2,
        private farmoutService: FarmoutService,
        private sharedDataService: SharedDataService
    ) {
        this.element = elRef.nativeElement;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.radius && !this.customPickerActive && this.googleAutocomplete) {
            this.initGooglePicker();
        }
    }

    ngOnInit() {
        this.getUser();
    }

    private getUser() {
        this.sharedDataService
            .getUser()
            .pipe(takeUntil(takeUntilDestroyed(this)))
            .subscribe((user) => {
                if (user && !this.isInitialized) {
                    this.user = user;
                    this.isInitialized = true;

                    if (user.isCustomAddressesPicker) {
                        this.initCustomPicker();
                    } else {
                        this.initGooglePicker();
                    }
                }
            });
    }

    private initCustomPicker() {
        this.customPickerActive = true;
        this.createDropdown();
        this.setupSuggestionLogic();
        this.setupClickOutside();
        this.setupResizeListener();
        this.setupManualChangeListener();
    }

    private cleanupCustomPicker() {
        this.customPickerActive = false;
        if (this.dropdown) {
            this.renderer.removeChild(document.body, this.dropdown);
            this.dropdown = null;
        }
    }

    private cleanupGooglePicker() {
        if (this.googleAutocomplete) {
            google.maps.event.clearInstanceListeners(this.googleAutocomplete);
            this.googleAutocomplete = null;
        }
    }

    private initGooglePicker() {
        this.customPickerActive = false;
        this.setupManualChangeListener();
        const options = this.radius ? this.setupGoogleOptions(this.radius) : {};
        this.googleAutocomplete = new google.maps.places.Autocomplete(this.element, options);

        google.maps.event.addListener(this.googleAutocomplete, 'place_changed', () => {
            const place = this.googleAutocomplete.getPlace();
            if (place) {
                this.onSelect.emit(place);
            }
        });
    }

    private setupGoogleOptions(radius) {
        const center = radius.center;
        if (!center) return {};
        const val = (radius.miles * 1.609344) / 100;

        const defaultBounds = {
            north: center.lat + val,
            south: center.lat - val,
            east: center.lng + val,
            west: center.lng - val,
        };

        return {
            bounds: defaultBounds,
            strictBounds: true
        };
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.cleanupCustomPicker();
        this.cleanupGooglePicker();
    }

    private createDropdown() {
        this.dropdown = this.renderer.createElement('div');
        this.renderer.addClass(this.dropdown, 'custom-place-dropdown');
        this.renderer.setStyle(this.dropdown, 'position', 'absolute');
        this.renderer.setStyle(this.dropdown, 'z-index', '1000');
        this.renderer.setStyle(this.dropdown, 'background', 'white');
        this.renderer.setStyle(this.dropdown, 'border', '1px solid #d1d5db');
        this.renderer.setStyle(this.dropdown, 'display', 'none');
        this.renderer.setStyle(this.dropdown, 'max-height', '300px');
        this.renderer.setStyle(this.dropdown, 'overflow-y', 'auto');
        this.renderer.setStyle(this.dropdown, 'box-shadow', '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)');
        this.renderer.setStyle(this.dropdown, 'font-family', 'inherit');
        this.renderer.setStyle(this.dropdown, 'border-radius', '4px');
        this.renderer.setStyle(this.dropdown, 'margin-top', '2px');
        document.body.appendChild(this.dropdown);
    }

    private updateDropdownPosition() {
        if (!this.dropdown || !this.element) return;
        const rect = this.element.getBoundingClientRect();
        this.renderer.setStyle(this.dropdown, 'top', `${rect.bottom + window.scrollY}px`);
        this.renderer.setStyle(this.dropdown, 'left', `${rect.left + window.scrollX}px`);

        const minWidth = Math.max(rect.width, 400);
        this.renderer.setStyle(this.dropdown, 'width', `${minWidth}px`);

        const viewportWidth = window.innerWidth;
        if (rect.left + minWidth > viewportWidth) {
            const adjustedLeft = Math.max(0, viewportWidth - minWidth - 20);
            this.renderer.setStyle(this.dropdown, 'left', `${adjustedLeft}px`);
        }
    }

    private setupResizeListener() {
        fromEvent(window, 'resize')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.customPickerActive && this.dropdown && this.dropdown.style.display !== 'none') {
                    this.updateDropdownPosition();
                }
            });
    }

    private setupSuggestionLogic() {
        fromEvent(this.element, 'input')
            .pipe(
                map(() => this.element.value),
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((query) => {
                    if (!this.customPickerActive || query.length < 4 || !this.user) {
                        this.hideDropdown();
                        return of({ addresses: [] });
                    }

                    return this.farmoutService.getAddress({ query, company: this.user.company ? this.user.company._id : this.user._id })
                        .pipe(
                            catchError(err => {
                                console.error('Address search error:', err);
                                return of({ addresses: [] });
                            })
                        );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((response: any) => {
                const suggestions = response && response.addresses ? response.addresses : [];
                this.renderSuggestions(suggestions);
            });
    }

    private renderSuggestions(suggestions: any[]) {
        if (!suggestions || suggestions.length === 0) {
            this.hideDropdown();
            return;
        }

        this.dropdown.innerHTML = '';
        suggestions.forEach((item, index) => {
            const option = this.renderer.createElement('div');
            this.renderer.addClass(option, 'custom-place-suggestion');
            this.renderer.setStyle(option, 'padding', '5px 8px');
            this.renderer.setStyle(option, 'cursor', 'pointer');
            this.renderer.setStyle(option, 'display', 'flex');
            this.renderer.setStyle(option, 'align-items', 'flex-start');
            if (index < suggestions.length - 1) {
                this.renderer.setStyle(option, 'border-bottom', '1px solid #ebebeb');
            }
            this.renderer.setStyle(option, 'transition', 'background-color 0.2s');

            const pinIcon = `
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 4px; color: #9aa0a6; flex-shrink: 0; margin-top: 1px;">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
                </svg>
            `;

            option.innerHTML = `
                ${pinIcon}
                <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; line-height: 20px; color: #70757a;">
                    <span style="font-weight: 700; color: #202124; margin-right: 4px;">${item.name || ''}</span>
                    <span>${item.full_address || ''}</span>
                </div>
            `;

            this.renderer.listen(option, 'click', () => {
                this.selectSuggestion(item);
            });

            this.renderer.listen(option, 'mouseenter', () => {
                this.renderer.setStyle(option, 'background-color', '#f1f3f4');
            });

            this.renderer.listen(option, 'mouseleave', () => {
                this.renderer.setStyle(option, 'background-color', 'transparent');
            });

            this.renderer.appendChild(this.dropdown, option);
        });

        const footer = this.renderer.createElement('div');
        this.renderer.setStyle(footer, 'display', 'flex');
        this.renderer.setStyle(footer, 'justify-content', 'flex-end');
        this.renderer.setStyle(footer, 'padding', '4px 14px');
        footer.innerHTML = `
            <span style="font-size: 11px; color: #70757a; opacity: 0.8;">powered by <span style="font-weight: bold; color: #4285f4;">yamsol</span></span>
        `;
        this.renderer.appendChild(this.dropdown, footer);

        this.showDropdown();
    }

    private selectSuggestion(item: any) {
        const address = item.full_address || item.address_line1 || item.name;
        this.isFromSuggestionSelection = true;
        this.element.value = address;

        const lat = item.latitude ? Number(item.latitude) : 0;
        const lng = item.longitude ? Number(item.longitude) : 0;

        const mappedPlace = {
            formatted_address: address,
            name: item.name,
            geometry: {
                location: {
                    lat: () => lat,
                    lng: () => lng,
                    latitude: lat,
                    longitude: lng
                }
            },
            address_components: this.tryExtractAddressComponents(item)
        };

        this.onSelect.emit(mappedPlace);
        this.hideDropdown();
        setTimeout(() => { this.isFromSuggestionSelection = false; }, 50);
    }

    private tryExtractAddressComponents(item: any): any[] {
        const components = [];
        const zipMatch = item.full_address ? item.full_address.match(/\b\d{5}(?:-\d{4})?\b/) : null;
        if (zipMatch) {
            components.push({
                long_name: zipMatch[0],
                short_name: zipMatch[0],
                types: ['postal_code']
            });
        }
        return components;
    }

    private showDropdown() {
        if (!this.customPickerActive) return;
        this.updateDropdownPosition();
        this.renderer.setStyle(this.dropdown, 'display', 'block');
    }

    private hideDropdown() {
        if (this.dropdown) {
            this.renderer.setStyle(this.dropdown, 'display', 'none');
        }
    }

    private setupClickOutside() {
        fromEvent(document, 'click')
            .pipe(takeUntil(this.destroy$))
            .subscribe((event: MouseEvent) => {
                if (this.customPickerActive && this.dropdown && !this.element.contains(event.target as Node) && !this.dropdown.contains(event.target as Node)) {
                    this.hideDropdown();
                }
            });
    }

    private setupManualChangeListener() {
        // Fires on paste, typing, backspace - emit with lat/lng cleared (debounce to batch rapid typing)
        fromEvent(this.element, 'input')
            .pipe(
                debounceTime(100),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                setTimeout(() => {
                    if (this.isFromSuggestionSelection) return;
                    const currentAddress = this.element.value;
                    const mappedPlace = {
                        formatted_address: currentAddress || '',
                        name: currentAddress || '',
                        geometry: {
                            location: {
                                lat: () => 0,
                                lng: () => 0,
                                latitude: 0,
                                longitude: 0
                            }
                        },
                        address_components: this.tryExtractAddressComponents({ full_address: currentAddress || '' })
                    };
                    this.onSelect.emit(mappedPlace);
                }, 0);
            });
    }
}
